import React, { useState, useEffect } from 'react';
import api from '../services/api';
import TabBar from '../components/TabBar';
import './Stats.css';

function getToken() { return window.__cafeApiToken || localStorage.getItem('cafepos_token'); }
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

// Data lokalna YYYY-MM-DD - NIE .toISOString() (przesuwa datę o dzień w Polsce,
// bo UTC+1/+2 sprawia że lokalna północ to jeszcze "wczoraj" w UTC)
function localISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function todayISO() { return localISO(new Date()); }
function daysAgoISO(n) { const d = new Date(); d.setDate(d.getDate() - n); return localISO(d); }

export default function Stats() {
  const [tab, setTab] = useState('today');

  return (
    <div className="stats-page">
      <TabBar
        tabs={[{ id: 'today', label: '📅 Dziś' }, { id: 'period', label: '📊 Okres' }]}
        active={tab} onChange={setTab}
      />
      {tab === 'today'  && <TodayStats />}
      {tab === 'period' && <PeriodStats />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Zakładka "Dziś" - dokładnie to co było wcześniej, bez zmian funkcjonalnych
// ---------------------------------------------------------------------------
function TodayStats() {
  const [date, setDate] = useState(todayISO());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/stats/today?date=${date}`, { headers: authH() })
      .then(r => r.json())
      .then(setStats)
      .finally(() => setLoading(false));
  }, [date]);

  const isToday = date === todayISO();

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)}
          style={{ padding: '7px 10px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--text)', fontSize: 13 }} />
        {!isToday && <button className="btn-sm" onClick={() => setDate(todayISO())}>Dziś</button>}
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {new Date(date + 'T00:00:00').toLocaleDateString('pl-PL', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
        </span>
      </div>

      {loading ? (
        <div className="page-loading">Ładowanie statystyk...</div>
      ) : !stats ? (
        <div className="no-data">Brak danych</div>
      ) : (
        <TodayStatsContent stats={stats} isToday={isToday} />
      )}
    </>
  );
}

function TodayStatsContent({ stats, isToday }) {
  const maxHour = Math.max(...(stats.byHour.map(h => Number(h.total)) || [1]));

  return (
    <>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="sc-label">{isToday ? 'Przychód dziś' : 'Przychód'}</div>
          <div className="sc-value">{Number(stats.revenue.total).toFixed(2)} zł</div>
        </div>
        <div className="stat-card">
          <div className="sc-label">Zamówień</div>
          <div className="sc-value">{stats.revenue.orders}</div>
        </div>
        <div className="stat-card">
          <div className="sc-label">Zajętość sali</div>
          <div className="sc-value">
            {isToday
              ? (stats.tableOcc.total > 0 ? Math.round(stats.tableOcc.occupied / stats.tableOcc.total * 100) : 0) + '%'
              : '—'}
          </div>
        </div>
        <div className="stat-card">
          <div className="sc-label">Śr. zamówienie</div>
          <div className="sc-value">
            {stats.revenue.orders > 0
              ? (Number(stats.revenue.total) / stats.revenue.orders).toFixed(2)
              : '0.00'} zł
          </div>
        </div>
      </div>

      <div className="chart-card">
        <h3>Przychód wg godziny</h3>
        {stats.byHour.length === 0
          ? <div className="no-data">Brak sprzedaży tego dnia</div>
          : (
            <div className="bar-chart">
              {stats.byHour.map(h => (
                <div key={h.hour} className="bar-wrap">
                  <div className="bar-val">{Number(h.total).toFixed(0)} zł<br /><span style={{ fontWeight: 400, fontSize: 10, color: 'var(--text-muted)' }}>{h.orders} rach.</span></div>
                  <div
                    className="bar"
                    style={{ height: `${Math.round(Number(h.total) / maxHour * 200)}px` }}
                  />
                  <div className="bar-label">{h.hour}:00</div>
                </div>
              ))}
            </div>
          )
        }
      </div>

      <div className="chart-card">
        <h3>Najpopularniejsze pozycje</h3>
        {stats.topItems.length === 0
          ? <div className="no-data">Brak danych</div>
          : stats.topItems.map((item, i) => {
            const max = stats.topItems[0].cnt;
            return (
              <div key={i} className="top-row">
                <span className="top-name">{item.name}</span>
                <div className="top-bar-wrap">
                  <div className="top-bar" style={{ width: `${Math.round(item.cnt / max * 100)}%` }} />
                </div>
                <span className="top-cnt">{item.cnt}×</span>
              </div>
            );
          })
        }
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Zakładka "Okres" - dowolny zakres dat, opcjonalny filtr po kategoriach i/lub
// pojedynczych produktach. Podsumowanie + wykres dzień-po-dniu + tabela pozycji.
// ---------------------------------------------------------------------------

function PeriodStats() {
  const [from, setFrom] = useState(daysAgoISO(29));
  const [to, setTo]     = useState(todayISO());
  const [menu, setMenu] = useState([]);
  const [selectedCats, setSelectedCats]   = useState(new Set());
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [itemSearch, setItemSearch]       = useState('');
  const [showFilters, setShowFilters]     = useState(false);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.getMenu().then(setMenu).catch(() => {}); }, []);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from, to });
      if (selectedItems.size) params.set('menu_item_ids', [...selectedItems].join(','));
      if (selectedCats.size)  params.set('category_ids', [...selectedCats].join(','));
      const res = await fetch(`/api/stats/period?${params}`, { headers: authH() });
      setData(await res.json());
    } catch { setData(null); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function toggleCat(id) {
    setSelectedCats(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleItem(id) {
    setSelectedItems(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function clearFilters() { setSelectedCats(new Set()); setSelectedItems(new Set()); }
  function setPreset(days) { setFrom(daysAgoISO(days - 1)); setTo(todayISO()); }
  function setThisMonth() {
    const d = new Date();
    setFrom(localISO(new Date(d.getFullYear(), d.getMonth(), 1)));
    setTo(todayISO());
  }
  function setLastMonth() {
    const d = new Date();
    setFrom(localISO(new Date(d.getFullYear(), d.getMonth() - 1, 1)));
    setTo(localISO(new Date(d.getFullYear(), d.getMonth(), 0)));
  }

  const allItems = menu.flatMap(cat => (cat.items || []).map(it => ({ ...it, category_name: cat.name })));
  const filteredItems = itemSearch
    ? allItems.filter(it => it.name.toLowerCase().includes(itemSearch.toLowerCase()))
    : allItems;
  const activeFilterCount = selectedCats.size + selectedItems.size;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)}
          style={{ padding: '7px 10px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--text)', fontSize: 13 }} />
        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>–</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)}
          style={{ padding: '7px 10px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--text)', fontSize: 13 }} />
        <button className="btn-sm" onClick={() => setPreset(7)}>7 dni</button>
        <button className="btn-sm" onClick={() => setPreset(30)}>30 dni</button>
        <button className="btn-sm" onClick={setThisMonth}>Ten miesiąc</button>
        <button className="btn-sm" onClick={setLastMonth}>Poprzedni miesiąc</button>
        <button className="btn-sm" onClick={() => setShowFilters(s => !s)}
          style={activeFilterCount > 0 ? { borderColor: 'var(--blue)', color: 'var(--blue)' } : undefined}>
          🔍 Filtr produktów{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </button>
        <button className="btn-primary" style={{ width: 'auto', padding: '7px 16px' }} onClick={load}>
          Zastosuj
        </button>
      </div>

      {showFilters && (
        <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>KATEGORIE</span>
            {activeFilterCount > 0 && (
              <button className="btn-sm" onClick={clearFilters}>Wyczyść filtry</button>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {menu.map(cat => (
              <label key={cat.id} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20,
                border: '0.5px solid var(--border)', cursor: 'pointer', fontSize: 13,
                background: selectedCats.has(cat.id) ? 'var(--blue-light)' : 'var(--bg)',
                color: selectedCats.has(cat.id) ? 'var(--blue-dark)' : 'var(--text)',
              }}>
                <input type="checkbox" checked={selectedCats.has(cat.id)} onChange={() => toggleCat(cat.id)} style={{ display: 'none' }} />
                {cat.name}
              </label>
            ))}
          </div>

          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>POJEDYNCZE PRODUKTY</div>
          <input value={itemSearch} onChange={e => setItemSearch(e.target.value)} placeholder="Szukaj produktu..."
            style={{ width: '100%', padding: '8px 12px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', fontSize: 13, marginBottom: 10, boxSizing: 'border-box' }} />
          <div style={{ maxHeight: 220, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filteredItems.map(it => (
              <label key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', cursor: 'pointer', fontSize: 13, borderRadius: 6 }}>
                <input type="checkbox" checked={selectedItems.has(it.id)} onChange={() => toggleItem(it.id)} />
                <span style={{ flex: 1 }}>{it.name}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{it.category_name}</span>
              </label>
            ))}
            {filteredItems.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: 8 }}>Brak wyników</div>}
          </div>
        </div>
      )}

      {loading ? (
        <div className="page-loading">Ładowanie...</div>
      ) : !data ? (
        <div className="no-data">Błąd ładowania danych</div>
      ) : (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="sc-label">Przychód w okresie</div>
              <div className="sc-value">{data.total.revenue.toFixed(2)} zł</div>
            </div>
            <div className="stat-card">
              <div className="sc-label">Sprzedanych sztuk</div>
              <div className="sc-value">{data.total.qty}</div>
            </div>
            <div className="stat-card">
              <div className="sc-label">Zamówień</div>
              <div className="sc-value">{data.total.orders}</div>
            </div>
            <div className="stat-card">
              <div className="sc-label">Śr. zamówienie</div>
              <div className="sc-value">
                {data.total.orders > 0 ? (data.total.revenue / data.total.orders).toFixed(2) : '0.00'} zł
              </div>
            </div>
          </div>

          <div className="chart-card">
            <h3>Przychód dzień po dniu</h3>
            {data.daily.length === 0 ? (
              <div className="no-data">Brak sprzedaży w tym okresie</div>
            ) : (
              <div className="bar-chart" style={{ overflowX: 'auto' }}>
                {data.daily.map(d => {
                  const max = Math.max(...data.daily.map(x => x.revenue), 1);
                  const [y, m, dd] = d.date.split('-').map(Number);
                  const weekday = new Date(y, m - 1, dd).toLocaleDateString('pl-PL', { weekday: 'short' });
                  return (
                    <div key={d.date} className="bar-wrap">
                      <div className="bar-val">{d.revenue.toFixed(0)}</div>
                      <div className="bar" style={{ height: `${Math.round(d.revenue / max * 200)}px` }} />
                      <div className="bar-label">{weekday}<br />{d.date.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="chart-card">
            <h3>Pozycje ({data.items.length})</h3>
            {data.items.length === 0 ? (
              <div className="no-data">Brak danych</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '0.5px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 6px', color: 'var(--text-muted)', fontWeight: 500 }}>Nazwa</th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', color: 'var(--text-muted)', fontWeight: 500 }}>Ilość</th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', color: 'var(--text-muted)', fontWeight: 500 }}>Przychód</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map(it => (
                    <tr key={it.id} style={{ borderBottom: '0.5px solid var(--border)' }}>
                      <td style={{ padding: '8px 6px' }}>{it.name}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right' }}>{it.qty}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 500 }}>{it.revenue.toFixed(2)} zł</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </>
  );
}
